/**
 * Device-ID normalization and validation for the public /track lookup flow
 * (External Pages v1, FE-1354). Copy strings are verbatim from the design handoff.
 */

export const DEVICE_ID_PATTERN = /^[A-Z0-9]{5,24}$/

export const DEVICE_ID_ERROR_EMPTY = 'Enter the device ID printed on the logger label.'

export const DEVICE_ID_ERROR_MALFORMED =
  "That doesn't look like a device ID. Use 5–24 letters and numbers, like ANM758404950495."

export const DEVICE_ID_HELPER_TEXT = 'Letters and numbers only. Dashes and spaces are ignored.'

const TRACK_DEEP_LINK_PARAM = 'd'

const isHrefUriMalformed = (href) => {
  try {
    decodeURI(href)
    return false
  } catch {
    return true
  }
}

/**
 * Fixes `/track?d=` deep links that contain URI-breaking characters (e.g. `#`, lone `%`)
 * so Clerk and the router can parse `window.location`. Only runs on `/track`; no-op
 * elsewhere. Call once at app bootstrap (via `ExternalPagesGate` import).
 */
export const sanitizeTrackDeepLinkUrl = () => {
  if (typeof window === 'undefined') return
  if (window.location.pathname !== '/track') return
  if (!isHrefUriMalformed(window.location.href)) return

  const rawSearch = window.location.search.startsWith('?')
    ? window.location.search.slice(1)
    : window.location.search

  let rawD = ''
  for (const part of rawSearch.split('&')) {
    if (!part) continue
    const eq = part.indexOf('=')
    const key = eq === -1 ? part : part.slice(0, eq)
    if (key === TRACK_DEEP_LINK_PARAM) {
      rawD = eq === -1 ? '' : part.slice(eq + 1)
      break
    }
  }

  // Browsers treat `#` as the fragment delimiter, so `?d=foo!@#$%^` often lands as
  // `?d=foo!@` + hash `$%^` — fold the fragment back into the device id for prefill.
  const hashSuffix = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
  if (hashSuffix && rawD) {
    rawD += hashSuffix
  }

  const safeParams = new URLSearchParams()
  if (rawD) {
    safeParams.set(TRACK_DEEP_LINK_PARAM, rawD)
  }

  const nextSearch = safeParams.toString()
  const nextUrl = `/track${nextSearch ? `?${nextSearch}` : ''}`
  window.history.replaceState(window.history.state, '', nextUrl)
}

/**
 * Normalizes raw device-ID input before validation/lookup: trims, uppercases, and strips
 * whitespace plus common separators (dash, dot, underscore, slash). Applied to manual
 * entry, scanned values, and `?d=` deep-link values identically.
 *
 * @param {string | null | undefined} raw
 * @returns {string} The normalized device ID (empty string when nothing remains).
 */
export const normalizeDeviceId = (raw) =>
  String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s\-._/]/g, '')

/**
 * Validates an already-normalized device ID.
 *
 * @param {string} normalized - Value returned by `normalizeDeviceId`.
 * @returns {{ valid: boolean, errorMessage: string | null }} Validity plus the inline error copy to show.
 */
export const validateDeviceId = (normalized) => {
  if (!normalized) {
    return { valid: false, errorMessage: DEVICE_ID_ERROR_EMPTY }
  }

  if (!DEVICE_ID_PATTERN.test(normalized)) {
    return { valid: false, errorMessage: DEVICE_ID_ERROR_MALFORMED }
  }

  return { valid: true, errorMessage: null }
}
